import React, {useEffect, useState, useRef} from "react";
import GenericLayout from "layouts/generic";
import css from "./index.module.scss";
import ContactSection from "sections/contact";
import { useInView } from "react-intersection-observer";
import { gsap } from "scripts/gsap";
import SplitText from "@/components/splitText";
import slideFadeIn from "scripts/animations/slideFadeIn";
import inViewSettings from "scripts/animations/inViewSettings";
import TacticsSmall, {TACTICS} from "@/sections/tacticsSmall";
import strapi from "scripts/strapi";
import TestimonialsSpotlight, {TESTIMONIALS} from "@/sections/testimonialsSpotlight";
import Fold1Section from "@/sections/fold1";
import BasicCardSection from "@/sections/basicCards";
import FlowChartSection from "@/sections/flowChart";
import MagnifyingGlassChartSvg from "/public/images/icons/magnifyingGlassChart.svg";
import FlaskVialSvg from "/public/images/icons/flaskVial.svg";
import ArrowsSpinSvg from "/public/images/icons/arrowsSpin.svg";
import Graphic1Svg from "./graphic1.svg";
import Graphic2Svg from "./graphic2.svg";

export default function LandingPageDevelopment({articles}) {
  const [elements, setElements] = useState({
    explanation: {
      text: useInView(inViewSettings(slideFadeIn)),
      headline: useInView(inViewSettings(slideFadeIn)),
    },
  });

  return <GenericLayout
    title="Boost Your Website Conversion Rates | Deviate Labs"
    description="Our expert ad team develops breakthrough creatives tailored to your brand's unique identity and goals."
    color="purple"
  >
    <Fold1Section
      headline={<>
        <div>Boost Your Website</div>
        <div>Conversion Rates</div>
      </>}
      className={css.fold}
      subheadline="Our data-driven approach takes the guesswork out of optimizing your website for more conversions."
      image={
        <div className={`${css.graphic}`} >
          <div className={css.funnels}>
            <div className={css.funnel}>
              <div className={css.label}>10% conversion rate</div>
              <Graphic1Svg/>
            </div>
            <div className={css.funnel}>
              <div className={css.label}>15% conversion rate</div>
              <Graphic2Svg/>
            </div>
          </div>

          <div className={css.explanation}><b>5%</b> increase in conversion rate → <b>125%</b> increase in sales</div>
        </div>
      }
    />


    <BasicCardSection
      headline="Small Changes Bring Big Gains"
      subheadline="Optimization isn't about drastic overhauls. It's about testing and fine-tuning elements on each page."
      width="3"
      color="cyan"
      cards={[
        {
          name: "Page Copy",
          body: <>We analyze page headlines, body copy, and calls-to-action. This includes tweaking language, adjusting length, improving readability, and ensuring messaging aligns with visitor intent. Refined copy grabs attention, builds trust, and motivates action.</>,
        },
        {
          name: "Media",
          body: <>Visual elements like product photos, videos, infographics and icons influence conversions. We test variations in media to determine optimal types, sizes, placements, and more. Well-designed visuals engage visitors and improve understanding.</>,
        },
        {
          name: "User Experience",
          body: <>Site navigation, page flows, form design, page speed, and more impact the user experience. We identify and correct issues to remove friction and bottlenecks. Improving UX leads to higher conversion rates.</>,
        },
      ]}
    />


    <FlowChartSection
      headline="The Optimization Process"
      subheadline="Our three-step process for conversion rate success."
      cards={[
        {
          headline: "Step 1: Analysis",
          body: "First, we audit your site, perform competitor analysis, and identify opportunities.",
          icon: <MagnifyingGlassChartSvg/>,
        },
        {
          headline: "Step 2: Testing",
          body: "Leveraging insights from Step 1, we implement changes and run A/B tests to identify optimal headlines, call-to-action placement, product images, and other variables.",
          icon: <FlaskVialSvg/>,
        },
        {
          headline: "Step 3: Iteration",
          body: "We take in the data from Step 2 and start over, creating an escalating feedback loop that continuously improves your conversion rate.",
          icon: <ArrowsSpinSvg/>,
        },
      ]}
    />



    <section className={`section ${css.explanation}`}>
      <div className="sectionInner">
        <h2 ref={elements.explanation.headline.ref}>
          <SplitText className="target" text="Real Business Results"/>
        </h2>

        <div className={css.text} ref={elements.explanation.text.ref}>
          <p>
            <SplitText className="target" text="Our scientific approach to optimization provides lasting gains for our clients. Rather than one-time quick fixes, we focus on sustainable strategies backed by data. The results speak for themselves through metrics like increased conversion rates, higher revenue, and improved ROI."/>
          </p>
          <p>
            <SplitText className="target" text="For example, we increased conversions by 31% and revenue by 120% for UpPaint, an upcycled paint company, by optimizing button text and placement. For a TMS therapy clinic, we tripled conversion rate from 2% to 6% with updates to landing page copy and social proof. The numbers add up, and our clients achieve significant returns on their optimization investment."/>
          </p>
          <p>
            <SplitText className="target" text="Beyond the metrics, our clients appreciate our rigorous CRO process. We take care of the heavy lifting so they can focus on core business priorities. Our team becomes an extension of theirs, laser-focused on driving continuous positive outcomes through testing and analysis."/>
          </p>
        </div>
      </div>
    </section>



    <TestimonialsSpotlight testimonial={TESTIMONIALS.TAPFILIATE}/>


    <TacticsSmall
      headline="We Offer a Full Range of Conversion Services"
      subheadline="Whatever your goals, we have the expertise to optimize your growth."
      enabled={[
        TACTICS.HIGH_PERFORMANCE_LANDING_PAGES,
        TACTICS.CONTENT_MARKETING,
        TACTICS.SONIC_BRANDING,
        TACTICS.INFLUENCER_MARKETING,
        TACTICS.B2B_SAAS_GROWTH_MARKETING,
      ]}
    />

    <ContactSection/>


  </GenericLayout>;
}


export async function getStaticProps() {
  let data = await strapi.article.findMany({
    filters: {
      id: {
        $eq: [33, 32, 27],
      },
    },
    populate: {
      coverArt: "*",
    },
    fields: [ //don't include the body since it's large and unnecessary
      "headline",
      "slug",
      "teaser",
      "priority",
      "category",
      "publishedAt",
    ],
  });

  return {
    props: {
      articles: data,
    },
    revalidate: 60,
  };
}
